import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Input, Button, Alert } from "antd";
import { notify } from "../ui/notify";
import AnotadorCaptura from "./AnotadorCaptura";
import { dataUrlABlob } from "./capturarPantalla";
import { construirMigas } from "./infoTecnica";
import { solicitudesCambioService, mensajeErrorEnvio } from "./solicitudesCambioService";
import "./solicitarCambio.css";

const TIPOS = [
  { key: "error", label: "Error", icon: "fa-bug" },
  { key: "visual", label: "Cambio visual", icon: "fa-paintbrush" },
  { key: "funcion", label: "Nueva función", icon: "fa-wand-magic-sparkles" },
  { key: "otro", label: "Otro", icon: "fa-ellipsis" },
];

const PRIORIDADES = [
  { key: "urgente", label: "Urgente" },
  { key: "normal", label: "Normal" },
  { key: "baja", label: "Cuando se pueda" },
];

const MAX_TITULO = 80;
const MIN_DESC = 20;
const MAX_EXTRAS = 5;

const FILAS_INFO = [
  ["Ruta actual", "ruta"],
  ["Título de la vista", "tituloVista"],
  ["Pestaña activa", "pestanaActiva"],
  ["Usuario", "usuario"],
  ["Rol", "rol"],
  ["Fecha y hora", "fechaHora"],
  ["Resolución de pantalla", "resolucion"],
  ["Versión de la app", "versionApp"],
];

export default function SolicitarCambioModal({ captura, info, onClose, onEnviado, tipoInicial = null }) {
  const anotadorRef = useRef(null);
  const fileRef = useRef(null);
  const extrasRef = useRef([]);

  const [titulo, setTitulo] = useState("");
  const [tipo, setTipo] = useState(tipoInicial);
  const [descripcion, setDescripcion] = useState("");
  const [prioridad, setPrioridad] = useState("normal");
  const [extras, setExtras] = useState([]); // [{ id, file, url }]
  const [nAnotaciones, setNAnotaciones] = useState(0);
  const [tocado, setTocado] = useState({});
  const [infoAbierta, setInfoAbierta] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState("");

  extrasRef.current = extras;
  useEffect(() => () => extrasRef.current.forEach((x) => URL.revokeObjectURL(x.url)), []);

  const marcar = (campo) => setTocado((t) => ({ ...t, [campo]: true }));

  const errores = useMemo(() => ({
    titulo: titulo.trim() ? "" : "Escribe un título corto.",
    tipo: tipo ? "" : "Selecciona el tipo de solicitud.",
    descripcion: descripcion.trim().length >= MIN_DESC
      ? ""
      : `La descripción necesita al menos ${MIN_DESC} caracteres (${descripcion.trim().length}/${MIN_DESC}).`,
  }), [titulo, tipo, descripcion]);
  const valido = !errores.titulo && !errores.tipo && !errores.descripcion;

  const agregarArchivos = useCallback((files) => {
    const imagenes = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!imagenes.length) return;
    const libres = MAX_EXTRAS - extrasRef.current.length;
    if (libres <= 0) { notify.warning(`Máximo ${MAX_EXTRAS} imágenes adicionales.`); return; }
    if (imagenes.length > libres) notify.warning(`Solo caben ${libres} imagen(es) más; se ignoró el resto.`);
    const nuevas = imagenes.slice(0, libres).map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      url: URL.createObjectURL(file),
    }));
    setExtras((prev) => [...prev, ...nuevas]);
  }, []);

  // Ctrl+V con imagen en el portapapeles (el texto pegado no se toca).
  useEffect(() => {
    const onPaste = (e) => {
      const files = Array.from(e.clipboardData?.files || []).filter((f) => f.type.startsWith("image/"));
      if (!files.length) return;
      e.preventDefault();
      agregarArchivos(files);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [agregarArchivos]);

  const quitarExtra = (id) => {
    setExtras((prev) => {
      const x = prev.find((p) => p.id === id);
      if (x) URL.revokeObjectURL(x.url);
      return prev.filter((p) => p.id !== id);
    });
  };

  const sucio = nAnotaciones > 0 || !!titulo.trim() || !!descripcion.trim() || extras.length > 0;

  const cancelar = () => {
    if (enviando) return;
    if (!sucio) { onClose(); return; }
    Modal.confirm({
      title: "¿Descartar la solicitud?",
      content: "Tienes anotaciones o texto escrito que se perderán.",
      okText: "Descartar",
      cancelText: "Seguir editando",
      okButtonProps: { danger: true },
      zIndex: 3100,
      centered: true,
      onOk: onClose,
    });
  };

  const enviar = async () => {
    setTocado({ titulo: true, tipo: true, descripcion: true });
    if (!valido || enviando) return;
    if (!captura) {
      setErrorEnvio("No hay captura de pantalla. Cierra esta ventana y vuelve a intentarlo.");
      return;
    }
    setEnviando(true);
    setErrorEnvio("");
    try {
      const imagenAnotada = await anotadorRef.current?.aplanar();
      const capturaOriginal = await dataUrlABlob(captura);
      const { folio } = await solicitudesCambioService.enviar({
        titulo: titulo.trim(),
        tipo,
        descripcion: descripcion.trim(),
        prioridad,
        imagenAnotada,
        capturaOriginal,
        imagenesAdicionales: extras.map((x) => x.file),
        infoTecnica: info,
      });
      onEnviado(folio);
    } catch (err) {
      const msg = mensajeErrorEnvio(err);
      setErrorEnvio(msg);
      notify.error(msg);
    } finally {
      setEnviando(false);
    }
  };

  const migas = construirMigas(info);
  const subtitulo = `Pantalla: ${migas.length ? migas.join(" › ") : info.ruta}`;

  return (
    <Modal
      open
      centered={false}
      width="90vw"
      zIndex={3000}
      className="sc-modal"
      rootClassName="sc-modal-root"
      maskClosable={false}
      keyboard={!enviando}
      closable={!enviando}
      onCancel={cancelar}
      destroyOnClose
      title={
        <div>
          <div className="sc-modal-title">Solicitar cambio</div>
          <div className="sc-modal-sub">{subtitulo}</div>
        </div>
      }
      footer={
        <div className="sc-footer">
          <Button onClick={cancelar} disabled={enviando}>Cancelar</Button>
          <Button
            type="primary"
            onClick={enviar}
            loading={enviando}
            disabled={!valido}
            style={valido ? { backgroundColor: "var(--eh-bg)", borderColor: "var(--eh-bg)" } : undefined}
          >
            Enviar solicitud
          </Button>
        </div>
      }
    >
      <div className="sc-layout">
        <section className="sc-col-captura">
          <AnotadorCaptura ref={anotadorRef} src={captura} onCambio={setNAnotaciones} />
        </section>

        <section className="sc-col-form">
          {errorEnvio && (
            <Alert type="error" showIcon message="No se pudo enviar la solicitud" description={`${errorEnvio} Tus datos siguen aquí.`} style={{ marginBottom: 12 }} />
          )}

          <div className="sc-field">
            <label className="sc-label" htmlFor="sc-titulo">Título corto *</label>
            <Input
              id="sc-titulo"
              value={titulo}
              maxLength={MAX_TITULO}
              showCount
              placeholder="Ej. Agregar campo de alergias en el expediente"
              status={tocado.titulo && errores.titulo ? "error" : undefined}
              onChange={(e) => setTitulo(e.target.value)}
              onBlur={() => marcar("titulo")}
              disabled={enviando}
            />
            {tocado.titulo && errores.titulo && <div className="sc-error">{errores.titulo}</div>}
          </div>

          <div className="sc-field">
            <div className="sc-label">Tipo *</div>
            <div className="sc-chips">
              {TIPOS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`sc-chip${tipo === t.key ? " sc-chip--active" : ""}`}
                  onClick={() => { setTipo(t.key); marcar("tipo"); }}
                  disabled={enviando}
                >
                  <i className={`fa-solid ${t.icon}`} /> {t.label}
                </button>
              ))}
            </div>
            {tocado.tipo && errores.tipo && <div className="sc-error">{errores.tipo}</div>}
          </div>

          <div className="sc-field">
            <label className="sc-label" htmlFor="sc-desc">Descripción *</label>
            <Input.TextArea
              id="sc-desc"
              value={descripcion}
              rows={5}
              placeholder="¿Qué pasa ahora? ¿Qué te gustaría que pasara? Entre más detalle, mejor."
              status={tocado.descripcion && errores.descripcion ? "error" : undefined}
              onChange={(e) => setDescripcion(e.target.value)}
              onBlur={() => marcar("descripcion")}
              disabled={enviando}
            />
            {tocado.descripcion && errores.descripcion && <div className="sc-error">{errores.descripcion}</div>}
          </div>

          <div className="sc-field">
            <div className="sc-label">Prioridad *</div>
            <div className="sc-chips">
              {PRIORIDADES.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`sc-chip${prioridad === p.key ? " sc-chip--active" : ""}`}
                  onClick={() => setPrioridad(p.key)}
                  disabled={enviando}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="sc-field">
            <div className="sc-label">
              Imágenes adicionales <span className="sc-label-hint">(opcional, {extras.length}/{MAX_EXTRAS})</span>
            </div>
            <div className="sc-extras">
              {extras.map((x) => (
                <div key={x.id} className="sc-thumb">
                  <img src={x.url} alt={x.file.name || "Imagen adicional"} />
                  <button type="button" className="sc-thumb-x" onClick={() => quitarExtra(x.id)} aria-label="Quitar imagen" disabled={enviando}>
                    <i className="fa-solid fa-xmark" />
                  </button>
                </div>
              ))}
              {extras.length < MAX_EXTRAS && (
                <button type="button" className="sc-thumb sc-thumb--add" onClick={() => fileRef.current?.click()} disabled={enviando}>
                  <i className="fa-solid fa-plus" />
                  <span>Subir</span>
                </button>
              )}
            </div>
            <div className="sc-hint">También puedes pegar una imagen con Ctrl+V.</div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => { agregarArchivos(e.target.files); e.target.value = ""; }}
            />
          </div>

          <div className="sc-field sc-info">
            <button type="button" className="sc-info-head" onClick={() => setInfoAbierta((v) => !v)} aria-expanded={infoAbierta}>
              <i className={`fa-solid fa-chevron-${infoAbierta ? "down" : "right"}`} />
              Información técnica
              <span className="sc-label-hint">(se llena sola)</span>
            </button>
            {infoAbierta && (
              <dl className="sc-info-list">
                {FILAS_INFO.map(([etiqueta, clave]) => (
                  <React.Fragment key={clave}>
                    <dt>{etiqueta}</dt>
                    <dd>{info[clave] || "—"}</dd>
                  </React.Fragment>
                ))}
              </dl>
            )}
          </div>
        </section>
      </div>
    </Modal>
  );
}
